import uhaulStore from "../../CustomRedux/UhaulStore";
import GetLiveChatHeader from "../../LiveChatHeaderHelper";
import { EncodedParams } from "../types/serverless";
import ApiService from "./ApiService";

type TaskAssignmentStatus = "reserved" | "assigned" | "canceled" | "wrapping" | "completed" | "transferring";

interface Queue {
  targetWorkers: string;
  friendlyName: string;
  sid: string;
}

interface UpdateTaskAttributesResponse {
  success: boolean;
}

interface GetQueuesResponse {
  success: boolean;
  queues: Array<Queue>;
}

interface WorkerChannelCapacityResponse {
  accountSid: string;
  assignedTasks: number;
  available: boolean;
  availableCapacityPercentage: number;
  configuredCapacity: number;
  dateCreated: string;
  dateUpdated: string;
  sid: string;
  taskChannelSid: string;
  taskChannelUniqueName: string;
  workerSid: string;
  workspaceSid: string;
  url: string;
}

interface GetWorkerChannelsResponse {
  success: boolean;
  workerChannels: Array<WorkerChannelCapacityResponse>;
}

interface UpdateWorkerChannelResponse {
  success: boolean;
  message?: string;
  workerChannelCapacity: WorkerChannelCapacityResponse;
}

let queues = null as null | Array<Queue>;
const formUrlEncoded = "application/x-www-form-urlencoded";

function returnResponse(response: UpdateTaskAttributesResponse): UpdateTaskAttributesResponse {
  return {
    ...response
  };
}

export default class TaskRouterService extends ApiService {
  async updateTaskAttributes(taskSid: string, attributesUpdate: object): Promise<boolean> {
    const result = await this.#updateTaskAttributes(taskSid, JSON.stringify(attributesUpdate));

    return result.success;
  }

  async updateTaskAssignmentStatus(taskSid: string, assignmentStatus: TaskAssignmentStatus): Promise<boolean> {
    const result = await this.#updateTaskAssignmentStatus(taskSid, assignmentStatus);

    return result.success;
  }

  /*
   * does a one time fetch for queues per session
   * since queue configuration seldom changes
   */
  async getQueues(force?: boolean): Promise<Array<Queue> | null> {
    if (queues && !force) return queues;

    const response = await this.#getQueues();
    // eslint-disable-next-line prefer-destructuring
    if (response.success) queues = response.queues;
    return queues;
  }

  async getWorkerChannels(workerSid: string): Promise<Array<WorkerChannelCapacityResponse>> {
    const response = await this.#getWorkerChannels(workerSid);
    if (response.success) return response.workerChannels;
    return [];
  }

  async updateWorkerChannel(
    workerSid: string,
    workerChannelSid: string,
    capacity: number,
    available: boolean
  ): Promise<boolean> {
    const result = await this.#updateWorkerChannel(workerSid, workerChannelSid, capacity, available);

    return result.success;
  }

  #updateTaskAssignmentStatus = async (
    taskSid: string,
    assignmentStatus: TaskAssignmentStatus
  ): Promise<UpdateTaskAttributesResponse> => {
    const encodedParams: EncodedParams = {
      Token: encodeURIComponent(this.manager.user.token),
      taskSid: encodeURIComponent(taskSid),
      assignmentStatus: encodeURIComponent(assignmentStatus)
    };

    return this.fetchJsonWithReject<UpdateTaskAttributesResponse>(
      `${
        uhaulStore.getState().UhaulReducer.currentUser.featuresBaseUrl
      }/common/flex/taskrouter/update-task-assignment-status`,
      {
        method: "post",
        headers: { "Content-Type": formUrlEncoded },
        body: this.buildBody(encodedParams)
      }
    ).then(returnResponse);
  };

  #updateTaskAttributes = async (taskSid: string, attributesUpdate: string): Promise<UpdateTaskAttributesResponse> => {
    const encodedParams: EncodedParams = {
      Token: encodeURIComponent(this.manager.user.token),
      taskSid: encodeURIComponent(taskSid),
      attributesUpdate: encodeURIComponent(attributesUpdate)
    };

    return this.fetchJsonWithReject<UpdateTaskAttributesResponse>(
      `${uhaulStore.getState().UhaulReducer.currentUser.featuresBaseUrl}/common/flex/taskrouter/update-task-attributes`,
      {
        method: "post",
        headers: { "Content-Type": formUrlEncoded },
        body: this.buildBody(encodedParams)
      }
    ).then(returnResponse);
  };

  #getQueues = async (): Promise<GetQueuesResponse> => {
    return this.fetchJsonWithReject<GetQueuesResponse>(
      `${uhaulStore.getState().UhaulReducer.config.liveChatApiUrl}/get-queues`,
      {
        method: "GET",
        headers: GetLiveChatHeader()
      }
    ).then((response): GetQueuesResponse => {
      return response;
    });
  };

  #getWorkerChannels = async (workerSid: string): Promise<GetWorkerChannelsResponse> => {
    const encodedParams: EncodedParams = {
      workerSid: encodeURIComponent(workerSid),
      Token: encodeURIComponent(this.manager.user.token)
    };

    return this.fetchJsonWithReject<GetWorkerChannelsResponse>(
      `${uhaulStore.getState().UhaulReducer.currentUser.featuresBaseUrl}/common/flex/taskrouter/get-worker-channels`,
      {
        method: "post",
        headers: { "Content-Type": formUrlEncoded },
        body: this.buildBody(encodedParams)
      }
    ).then((response): GetWorkerChannelsResponse => {
      return response;
    });
  };

  #updateWorkerChannel = async (
    workerSid: string,
    workerChannelSid: string,
    capacity: number,
    available: boolean
  ): Promise<UpdateWorkerChannelResponse> => {
    const encodedParams: EncodedParams = {
      Token: encodeURIComponent(this.manager.user.token),
      workerSid: encodeURIComponent(workerSid),
      workerChannelSid: encodeURIComponent(workerChannelSid),
      capacity: encodeURIComponent(capacity),
      available: encodeURIComponent(available)
    };

    return this.fetchJsonWithReject<UpdateWorkerChannelResponse>(
      `${uhaulStore.getState().UhaulReducer.currentUser.featuresBaseUrl}/common/flex/taskrouter/update-worker-channel`,
      {
        method: "post",
        headers: { "Content-Type": formUrlEncoded },
        body: this.buildBody(encodedParams)
      }
    ).then((response): UpdateWorkerChannelResponse => {
      return response;
    });
  };
}
